import { beautifyLinkText, formatNumber, isClickableLink } from '@fulcrum/lib';
import {
  CustomPropertyInputType,
  CustomPropertyType,
} from '@data-access/connect/tc-pset';
import { t } from '@trimble/i18n';
import { asError, getLogger } from '@trimble/logging';
import { formatDate, toMedDateTimeString } from '@trimble/date-utils';
import type { ObjectPropertiesPanelValue } from '@trimble/web3d-capabilities';
import { UnreachableCaseError } from 'ts-essentials';
import type { CustomPanelProperty } from './object-properties.types';
import {
  formatMeasureDisplay,
  resolveMeasureSourceUnits,
} from './object-properties-measure-formatting';
import {
  toDate,
  type ObjectPropertyFormattingContext,
} from './object-properties-formatting-helper';

/**
 * Custom (property-set-library) values reuse tcweb's `--` placeholder for
 * missing values instead of the em dash used for model properties, matching
 * the React property panel.
 */
const CUSTOM_EMPTY_VALUE_PLACEHOLDER = '--';

type CustomPropInputType = CustomPanelProperty['inputType'];
type CustomPropScalar = Exclude<
  CustomPanelProperty['value'],
  readonly unknown[] | undefined | null
>;

/**
 * @intent Formats a single custom (property-set-library) property value by its
 * schema-derived type, mirroring tcweb's `@fulcrum/cdm-components`
 * `formatServicePropValue`:
 *  - unsupported/auto-calculated types render as "Unknown"
 *  - missing values render as "--" (except booleans, which render "Unknown")
 *  - list values format each item and join them with ", "
 *  - measures convert from their base unit and append the project unit symbol
 */
export function formatCustomPropValue(
  prop: Pick<CustomPanelProperty, 'value' | 'propType' | 'inputType'>,
  formatting: ObjectPropertyFormattingContext
): string {
  const { propType, inputType, value } = prop;

  if (
    propType === CustomPropertyType.Unknown ||
    propType === CustomPropertyType.AutoCalculate
  ) {
    return t('objectProperties.unknown');
  }

  if (
    (value === undefined || value === null) &&
    propType !== CustomPropertyType.Logical
  ) {
    return CUSTOM_EMPTY_VALUE_PLACEHOLDER;
  }

  if (propType === CustomPropertyType.ListValue) {
    // API typically omits empty lists (null/undefined), but guard `[]` too so
    // the panel shows the same missing-value placeholder instead of blank text.
    if (Array.isArray(value) && value.length > 0) {
      return value
        .map((item) => formatCustomSingleValue(item, inputType, formatting))
        .join(', ');
    }
    return CUSTOM_EMPTY_VALUE_PLACEHOLDER;
  }

  return formatCustomSingleValue(value, inputType, formatting);
}

/**
 * Returns an href when the formatted display value is a clickable link, so the
 * panel can render it as an anchor (matching tcweb's `PsetValueRow`).
 */
export function getCustomPropLinkHref(
  displayValue: string
): string | undefined {
  return isClickableLink(displayValue) ? displayValue : undefined;
}

/** Human-friendly link text for a URL (strips protocol/trailing slash). */
export function beautifyCustomPropLink(displayValue: string): string {
  return beautifyLinkText(displayValue);
}

function formatCustomSingleValue(
  value: unknown,
  inputType: CustomPropInputType,
  formatting: ObjectPropertyFormattingContext
): string {
  try {
    if (Array.isArray(value)) return t('objectProperties.unknownValue');

    const settings = formatting.settings;
    const measureSourceUnits = resolveMeasureSourceUnits(
      formatting.sourceUnits
    );
    switch (inputType) {
      case CustomPropertyInputType.Text:
      case CustomPropertyInputType.URI:
        return String(value);
      case CustomPropertyInputType.LengthMeasure:
        return formatFiniteNumber(value, (numeric) =>
          formatMeasureDisplay('length', numeric, settings, measureSourceUnits)
        );
      case CustomPropertyInputType.AreaMeasure:
        return formatFiniteNumber(value, (numeric) =>
          formatMeasureDisplay('area', numeric, settings, measureSourceUnits)
        );
      case CustomPropertyInputType.VolumeMeasure:
        return formatFiniteNumber(value, (numeric) =>
          formatMeasureDisplay('volume', numeric, settings, measureSourceUnits)
        );
      case CustomPropertyInputType.MassMeasure:
        return formatFiniteNumber(value, (numeric) =>
          formatMeasureDisplay('mass', numeric, settings, {})
        );
      case CustomPropertyInputType.AngleMeasure:
        return formatFiniteNumber(value, (numeric) =>
          formatMeasureDisplay('angle', numeric, settings, measureSourceUnits)
        );
      case CustomPropertyInputType.NumericWhole:
        return formatFiniteNumber(value, (numeric) => formatNumber(numeric, 0));
      case CustomPropertyInputType.NumericDecimal:
        return formatFiniteNumber(value, (numeric) =>
          formatNumber(numeric, settings.genericDecimals)
        );
      case CustomPropertyInputType.Boolean:
      case CustomPropertyInputType.Logical:
        return formatLogical(value);
      case CustomPropertyInputType.Date:
        return formatSchemaDate(value);
      case CustomPropertyInputType.DateTime:
        return formatSchemaDateTime(value);
      case CustomPropertyInputType.Unknown:
      case CustomPropertyInputType.Duration:
      case CustomPropertyInputType.Query:
        // Valid schema types we intentionally do not format yet.
        return t('objectProperties.unknownValue');
      default:
        throw new UnreachableCaseError(inputType);
    }
  } catch (error) {
    // Keep UnreachableCaseError loud (including from formatMeasureDisplay);
    // swallow only unexpected formatter failures.
    if (error instanceof UnreachableCaseError) throw error;
    getLogger().warn(
      'Failed to format custom property value',
      { inputType },
      asError(error)
    );
    return t('objectProperties.unknownValue');
  }
}

/**
 * Custom logical/boolean values (tcweb parity). Kept separate from
 * `coerceBoolean`, which coerces model wire shapes (0/1, strings). Missing
 * custom booleans (Logical or Boolean) render "Unknown"; model null/undefined
 * uses "—".
 */
function formatLogical(value: unknown): string {
  if (value === undefined || value === null) {
    return t('objectProperties.unknown');
  }
  return value ? t('objectProperties.true') : t('objectProperties.false');
}

function asFiniteNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function formatFiniteNumber(
  value: unknown,
  format: (numeric: number) => string
): string {
  const numeric = asFiniteNumber(value);
  return numeric !== undefined
    ? format(numeric)
    : t('objectProperties.unknownValue');
}

/**
 * Schema `date` values are calendar strings (`YYYY-MM-DD`). Format the string
 * directly so we never round-trip through Date/UTC (which can shift the day).
 */
function formatSchemaDate(value: unknown): string {
  if (typeof value !== 'string') return String(value as CustomPropScalar);
  return formatDate(value);
}

/** Schema `date-time` values are usually ISO strings; `toDate` covers wire shapes. */
function formatSchemaDateTime(value: unknown): string {
  const wireDate = toDate(value as ObjectPropertiesPanelValue);
  const date =
    wireDate ??
    (typeof value === 'string' || typeof value === 'number'
      ? new Date(value)
      : undefined);
  if (date === undefined || Number.isNaN(date.getTime())) {
    return String(value as CustomPropScalar);
  }
  return toMedDateTimeString(date);
}
